import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Receipt, RotateCcw, ShieldAlert, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { watchTransactions, formatAmount, type StoredTransaction } from '../services/paymentsService';

/**
 * Billing page — per-user ledger of Stripe activity.
 *
 * Reads directly from users/{uid}/transactions via Firestore; writes happen
 * on the backend through the Stripe webhook, which is the only path that
 * can move credits. That means this page can never show a transaction that
 * didn't actually land in a webhook — good for audit/support.
 */
export default function Billing() {
  const { user } = useAuth();
  const [items, setItems] = useState<StoredTransaction[] | null>(null);

  useEffect(() => {
    const unsub = watchTransactions(setItems);
    return () => unsub();
  }, []);

  if (!user) return null;

  return (
    <div className="container-wide py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold">Billing</h1>
          <p className="text-ink-600 mt-1">Your payments, refunds, and account activity.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="chip bg-brand-50 text-brand-700 border border-brand-100 text-sm px-3 py-1.5">
            <Coins className="w-4 h-4 mr-1.5" /> {user.credits} credits
          </span>
          <Link to="/pricing" className="btn-secondary btn-sm">
            <Plus className="w-4 h-4" /> Top up
          </Link>
        </div>
      </div>

      <section className="mt-8 card">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-ink-100">
          <h2 className="font-semibold">Transaction history</h2>
          <span className="text-xs text-ink-500">Most recent 100</span>
        </div>

        {items === null ? (
          <div className="p-10 text-center text-ink-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading your history…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink-500 text-sm">
            No transactions yet. Your first top-up will appear here within a minute of payment.
          </div>
        ) : (
          <ul className="divide-y divide-ink-100">
            {items.map((t) => <TransactionRow key={t.id} t={t} />)}
          </ul>
        )}
      </section>

      <p className="mt-4 text-xs text-ink-500">
        Need a tax invoice or a refund? Email{' '}
        <a href="mailto:support@gradelab.club" className="text-brand-700 hover:underline">
          support@gradelab.club
        </a>{' '}
        with the payment ID shown on the row.
      </p>
    </div>
  );
}

function TransactionRow({ t }: { t: StoredTransaction }) {
  const d = new Date(t.createdAt);
  const date = d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const icon =
    t.type === 'payment'  ? <Receipt      className="w-4 h-4" />
  : t.type === 'refund'   ? <RotateCcw    className="w-4 h-4" />
  :                          <ShieldAlert className="w-4 h-4" />;

  const tone =
    t.type === 'payment'  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
  : t.type === 'refund'   ? 'bg-amber-50   text-amber-700   border-amber-100'
  :                          'bg-red-50     text-red-700     border-red-100';

  const creditsLabel =
    t.creditsDelta > 0 ? `+${t.creditsDelta} credits`
  : t.creditsDelta < 0 ? `${t.creditsDelta} credits`
  :                       'no credit change';

  return (
    <li className="px-6 py-4 flex items-center gap-4">
      <div className={`w-9 h-9 grid place-items-center rounded-xl border ${tone}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-ink-900">
            {t.description || defaultDescription(t)}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>
            {t.type}
          </span>
        </div>
        <div className="mt-1 text-xs text-ink-500 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>{date}</span>
          <span>{creditsLabel}</span>
          {t.paymentIntentId && (
            <span className="font-mono truncate max-w-[220px]" title={t.paymentIntentId}>
              {t.paymentIntentId}
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className={`font-semibold ${t.amount < 0 ? 'text-amber-700' : 'text-ink-900'}`}>
          {formatAmount(t.amount, t.currency)}
        </div>
        <div className="text-[11px] text-ink-500 uppercase tracking-wide">
          {t.currency?.toUpperCase()}
        </div>
      </div>
    </li>
  );
}

function defaultDescription(t: StoredTransaction): string {
  if (t.type === 'payment') return `Purchased ${t.planId ?? 'credits'}`;
  if (t.type === 'refund')  return 'Refund from Stripe';
  return 'Chargeback opened';
}
